import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'Privacy Policy', path: '/privacy-policy' }]} />

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Privacy Policy</h1>
          <p className="text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className="space-y-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Datatomic Labs ("we," "us," or "our") operates Options Analogies (the "Site"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
              </p>
            </CardContent>
          </Card>

          {/* Information Collection */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Information We Collect</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Automatically Collected Information</h3>
                  <p className="mb-2">When you visit our Site, we automatically collect certain information about your device, including:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>IP address</li>
                    <li>Pages visited and time spent</li>
                    <li>Referring website</li>
                    <li>Device identifiers</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Information You Provide</h3>
                  <p className="mb-2">We may collect information you voluntarily provide when you:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Contact us via email</li>
                    <li>Subscribe to newsletters or updates</li>
                    <li>Participate in surveys or feedback forms</li>
                    <li>Download educational materials</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">How We Use Your Information</h2>
              <p className="text-gray-300 mb-3">We use the collected information to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                <li>Provide, maintain, and improve our Site</li>
                <li>Understand how visitors use our Site and educational content</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send educational updates and newsletters (with your consent)</li>
                <li>Analyze usage patterns and optimize user experience</li>
                <li>Prevent fraud and ensure Site security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cookies and Tracking */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Cookies and Tracking Technologies</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  We use cookies and similar tracking technologies to track activity on our Site and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                </p>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Types of Cookies We Use:</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Essential Cookies:</strong> Required for Site functionality</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand Site usage patterns</li>
                    <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Third-Party Services */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Third-Party Services</h2>
              <div className="space-y-3 text-gray-300">
                <p>We may use third-party services that collect, monitor, and analyze information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Google Analytics:</strong> To track and analyze Site traffic</li>
                  <li><strong>Google AdSense:</strong> To display advertisements on our Site</li>
                  <li><strong>Email Service Providers:</strong> To send newsletters and updates</li>
                </ul>
                <p className="mt-3">
                  These third parties have their own privacy policies. We encourage you to review their policies before engaging with their services.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Google AdSense */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Google AdSense</h2>
              <div className="space-y-3 text-gray-300">
                <p>
                  We use Google AdSense to display advertisements on our Site. Google uses cookies to serve ads based on your prior visits to our Site or other websites.
                </p>
                <p>
                  You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">Google Ads Settings</a>.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Information Sharing and Disclosure</h2>
              <div className="space-y-3 text-gray-300">
                <p>We do not sell your personal information. We may share information in the following circumstances:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Service Providers:</strong> With trusted third parties who assist in Site operations</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement reasonable security measures to protect your information. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Your Privacy Rights</h2>
              <div className="space-y-3 text-gray-300">
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access your personal information we hold</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to or restrict processing of your data</li>
                  <li>Data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
                <p className="mt-3">
                  To exercise these rights, please contact us at <a href="mailto:contact@datatomiclabs.com" className="text-emerald-400 hover:text-emerald-300 underline">contact@datatomiclabs.com</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our Site is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </CardContent>
          </Card>

          {/* Changes */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Site after changes constitutes acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
              <p className="text-gray-300 leading-relaxed">
                If you have questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 text-gray-300">
                <p><strong className="text-white">Email:</strong> <a href="mailto:contact@datatomiclabs.com" className="text-emerald-400 hover:text-emerald-300 underline">contact@datatomiclabs.com</a></p>
                <p className="mt-2"><strong className="text-white">Company:</strong> Datatomic Labs</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;